/* eur-home-o2.js (2026-09-27): shared behaviour for the homepage "Option 2" modules. Homepage only, loaded with
   defer from snippets/eur-home-assets.liquid. Vanilla, no dependencies, and everything is opt-in through data
   attributes, so it no-ops on markup that does not ask for it. Content is complete without it: rails are native
   scroll-snap, every tab panel is server-rendered, forms post to /cart/add.

   DATA-ATTRIBUTE API
   Scope        [data-o2-scope]            groups one module's tabs, arrows and rails (default: the Shopify section)
   Tabs         [data-o2-tabs]             on the role="tablist" element. Children: [role=tab][aria-controls=<panel id>];
                                           panels: role="tabpanel", hidden when not selected. Click, Arrow keys, Home,
                                           End; roving tabindex; the selected tab scrolls into view; the panel's rails
                                           reset to the start. Fires "o2:tab" on the tablist, detail {index, tab, panel}.
   Rails        [data-o2-rail]             a horizontal scroller (ul.o2-rail)
   Arrows       [data-o2-dir="-1" | "1"]   buttons inside the scope: scroll the scope's VISIBLE rail by 80% of its width;
                                           aria-disabled="true" at either end
   Dots         [data-o2-dots="<id>"]      container of buttons for the snap scroller with that id (one button per
                                           child); aria-current="true" follows the swipe, a tap scrolls to that child
   Reveal       [data-rv]                  gets .in once 15% is in view, .done 2.6s later (data-rv-done="<ms>" to
                                           change); reduced motion or no IntersectionObserver = .in at once. This script
                                           adds html.o2-js when it starts: scope any pre-hidden entrance state to
                                           html.o2-js (or the theme's html.js) so nothing is hidden without JS.
   Fit          img[data-o2-fit], img[data-fit]   measures the product's ink box on a 160px copy (alpha > 24 and
                                           min(r,g,b) < 242, the eu-fit rule) and sets --x0 --y0 --wf --hf (fractions),
                                           --ia (ink aspect) and --ar (image aspect) on the img's PARENT, plus .is-fit.
                                           CSS decides what to do with them (.o2-card__stage does a fitted contain).
                                           data-fit="alpha" (transparent cut-outs): the ink box is alpha > 24 only, so
                                           white parts of a machine are kept; if the image turns out to be opaque it is
                                           measured by the eu-fit rule instead and the parent swaps .is-cut for .is-card.
                                           Kill switch: ?eufit=0 or window.EUR_O2 = {fit:false} before this loads.
   Add to cart  form[data-o2-atc]          input[name=id] + button[type=submit][data-title]: POST /cart/add.js, then the
                                           theme's product:added event, which refreshes and opens the cart drawer. The
                                           button shows .is-done (tick) / .is-error for a moment; a polite live region
                                           announces the result. The drawer opens only when the theme's Cart type is
                                           Drawer (the store runs Cart type = Message, like the old rails: count updates).
   Theme editor: re-initialises a section on shopify:section:load; selecting a tab block (the tab button carries
   block.shopify_attributes) selects that tab.

   ADDED 2026-10 (V4 redesign, additive: nothing above changed)
   Ask Otto     [data-o2-otto]             a button that opens Otto, the euronics-ai app's sales assistant:
                                           window.EuronicsAI.open({ prefill | submit, source, opener }), else a click on
                                           the app's launcher (.eai-launcher__open), else it goes to
                                           data-o2-otto-href (default /pages/ai). Options: data-o2-otto-prefill="text"
                                           (fills the question box), data-o2-otto-submit="question" (sends it),
                                           data-o2-otto-source="home_popular" (analytics label). Delegated, so cards the
                                           theme editor or a script adds later work too.
   Otto-only    [data-o2-otto-only]        gets the hidden attribute 3s after DOMContentLoaded when the app never
                                           appeared (app embed off), so no Otto button is left that cannot open Otto.
                                           Never in the theme editor.

   ADDED 2026-10-10 (review: product images are never blended onto a tint; additive)
   Cut-out      img[data-o2-cutout-check]  for a product picture on a white plate inside a tinted tile. Once the picture
                                           has loaded (and the browser is idle), the same Shopify image URL is loaded at
                                           width=48 (crossOrigin anonymous) and drawn to a canvas: if any of its four
                                           corner pixels has alpha < 250 it is a transparent cut-out, and the plate gets
                                           .is-cutout (CSS drops the white plate; its padding stays, so nothing moves).
                                           The plate = the closest [data-o2-plate], else the img's parent. Opaque photo,
                                           no JS, a CORS / canvas failure = the plate stays white. One probe per URL
                                           (width ignored); a picture that swaps source (<picture> at a breakpoint) is
                                           checked again for its new URL. Purely visual. */
(function () {
  'use strict';
  var d = document, w = window, root = d.documentElement;
  var CFG = w.EUR_O2 || {};
  var mq = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }
  function beh() { return reduced() ? 'auto' : 'smooth'; }
  function all(sel, ctx) { return Array.prototype.slice.call((ctx || d).querySelectorAll(sel)); }
  function scopeOf(el) { return el.closest('[data-o2-scope]') || el.closest('.shopify-section') || d.body; }
  function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }

  /* ------------------------------------------------------------ rails + arrows */
  function visibleRail(scope) {
    var rails = all('[data-o2-rail]', scope);
    for (var i = 0; i < rails.length; i++) if (visible(rails[i])) return rails[i];
    return null;
  }
  function syncArrows(scope) {
    var btns = all('[data-o2-dir]', scope);
    if (!btns.length) return;
    var r = visibleRail(scope);
    var max = r ? r.scrollWidth - r.clientWidth : 0;
    btns.forEach(function (b) {
      var dir = +b.getAttribute('data-o2-dir');
      var off = !r || max <= 1 || (dir < 0 ? r.scrollLeft <= 1 : r.scrollLeft >= max - 1);
      b.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
  }
  function initScope(scope) {
    if (!scope || scope.__o2) return;
    scope.__o2 = true;
    all('[data-o2-dir]', scope).forEach(function (b) {
      b.addEventListener('click', function () {
        var r = visibleRail(scope);
        if (!r || b.getAttribute('aria-disabled') === 'true') return;
        r.scrollBy({ left: r.clientWidth * 0.8 * (+b.getAttribute('data-o2-dir') || 1), behavior: beh() });
      });
    });
    var raf = 0;
    function queue() { if (!raf) raf = w.requestAnimationFrame(function () { raf = 0; syncArrows(scope); }); }
    all('[data-o2-rail]', scope).forEach(function (r) { r.addEventListener('scroll', queue, { passive: true }); });
    w.addEventListener('resize', queue);
    scope.addEventListener('o2:tab', queue);
    syncArrows(scope);
  }

  /* ------------------------------------------------------------ tabs */
  function initTabs(tl) {
    if (tl.__o2) return;
    tl.__o2 = true;
    var tabs = all('[role=tab]', tl);
    function panelOf(t) { return d.getElementById(t.getAttribute('aria-controls')); }
    function select(i, focus) {
      if (i < 0 || i >= tabs.length) return;
      var panel = null;
      tabs.forEach(function (t, j) {
        var on = i === j, p = panelOf(t);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (p) {
          p.hidden = !on;
          if (on) {
            panel = p;
            all('[data-o2-rail]', p).forEach(function (r) { r.scrollLeft = 0; });
          }
        }
      });
      if (focus) tabs[i].focus({ preventScroll: true });
      /* bring the tab into view inside its own scroller only (never scroll the page), as the mockup does:
         - pointer: scrollIntoView({inline:'nearest'}), so a tab that is partly out of view ends flush with the strip's
           edge (no extra room) and a tab already in view does not move;
         - keyboard: the mockup's focus() scrolls first (Chrome's centre-if-needed: a tab entirely out of view is
           centred, a partly visible one goes to the nearest edge), then the same 'nearest'. Here the nearest edge keeps
           the width of the tab's focus ring (outline + offset; 0 while the theme hides the ring) inside the strip,
           so the ring is never cut off.
         Exact sub-pixel geometry (getBoundingClientRect, not the rounded offsetWidth). */
      var sc = tl, t = tabs[i];
      if (sc.scrollWidth > sc.clientWidth + 1) {
        var sb = sc.getBoundingClientRect(), tb = t.getBoundingClientRect();
        var l = tb.left - sb.left + sc.scrollLeft, r = l + tb.width, v0 = sc.scrollLeft, v1 = v0 + sc.clientWidth, to = null, pad = 0;
        if (focus) {
          var cs = w.getComputedStyle(t);
          if (cs.outlineStyle !== 'none') pad = Math.max(0, (parseFloat(cs.outlineWidth) || 0) + (parseFloat(cs.outlineOffset) || 0));
        }
        if (focus && (r <= v0 || l >= v1)) to = l + tb.width / 2 - sc.clientWidth / 2;
        else if (l - pad < v0) to = l - pad;
        else if (r + pad > v1) to = r + pad - sc.clientWidth;
        if (to !== null) sc.scrollTo({ left: Math.max(0, Math.min(to, sc.scrollWidth - sc.clientWidth)), behavior: beh() });
      }
      tl.dispatchEvent(new CustomEvent('o2:tab', { bubbles: true, detail: { index: i, tab: t, panel: panel } }));
    }
    tl.__o2select = select;
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(i); });
      t.addEventListener('keydown', function (e) {
        var n = null, k = e.key;
        if (k === 'ArrowRight' || k === 'ArrowDown') n = (i + 1) % tabs.length;
        else if (k === 'ArrowLeft' || k === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
        else if (k === 'Home') n = 0;
        else if (k === 'End') n = tabs.length - 1;
        if (n === null) return;
        e.preventDefault();
        select(n, true);
      });
    });
  }

  /* ------------------------------------------------------------ dots for a snap scroller */
  function initDots(box) {
    if (box.__o2) return;
    var sc = d.getElementById(box.getAttribute('data-o2-dots'));
    if (!sc) return;
    box.__o2 = true;
    var dots = all('button', box);
    function items() { return Array.prototype.slice.call(sc.children); }
    function cur() {
      var it = items(), best = 0, dist = 1e9, x0 = it[0] ? it[0].offsetLeft : 0;
      it.forEach(function (el, i) { var x = Math.abs(el.offsetLeft - x0 - sc.scrollLeft); if (x < dist) { dist = x; best = i; } });
      dots.forEach(function (b, i) { b.setAttribute('aria-current', i === best ? 'true' : 'false'); });
    }
    var raf = 0;
    sc.addEventListener('scroll', function () { if (!raf) raf = w.requestAnimationFrame(function () { raf = 0; cur(); }); }, { passive: true });
    dots.forEach(function (b, i) {
      b.addEventListener('click', function () {
        var it = items();
        if (it[i]) sc.scrollTo({ left: it[i].offsetLeft - it[0].offsetLeft, behavior: beh() });
      });
    });
    cur();
  }

  /* ------------------------------------------------------------ packshot fit (ink box) */
  var FIT_ON = CFG.fit !== false && !/[?&]eufit=0(&|$)/.test(w.location.search);
  var FKEY = 'eur-o2-fit-1', fcache = {};
  try { fcache = JSON.parse(w.sessionStorage.getItem(FKEY) || '{}') || {}; } catch (e) { fcache = {}; }
  var fsaveT = 0;
  function fsave() {
    clearTimeout(fsaveT);
    fsaveT = setTimeout(function () { try { w.sessionStorage.setItem(FKEY, JSON.stringify(fcache)); } catch (e) {} }, 800);
  }
  function fkey(src) { return src.replace(/^https?:/, '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, ''); }
  function applyFit(img, m) {
    var st = img.parentNode && img.parentNode.style;
    if (!st) return;
    st.setProperty('--x0', m[0]); st.setProperty('--y0', m[1]);
    st.setProperty('--wf', m[2]); st.setProperty('--hf', m[3]);
    st.setProperty('--ia', m[4]); st.setProperty('--ar', m[5]);
    img.parentNode.classList.add('is-fit');
    if (m[6]) { img.parentNode.classList.remove('is-cut'); img.parentNode.classList.add('is-card'); }
  }
  function fit(img) {
    var src = img.currentSrc || img.src;
    if (!src) return;
    var alpha = img.getAttribute('data-fit') === 'alpha';
    var k = (alpha ? 'a:' : '') + fkey(src);
    if (fcache[k]) { applyFit(img, fcache[k]); return; }
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var W = 160, H = Math.max(1, Math.round(160 * probe.naturalHeight / probe.naturalWidth));
        var c = d.createElement('canvas'); c.width = W; c.height = H;
        var x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(probe, 0, 0, W, H);
        var p = x.getImageData(0, 0, W, H).data, x0 = W, y0 = H, x1 = -1, y1 = -1;
        /* an "alpha" image with fully opaque corners is not really a cut-out: measure it like a packshot */
        var opaque = alpha && p[3] > 250 && p[(W - 1) * 4 + 3] > 250 && p[((H - 1) * W) * 4 + 3] > 250 && p[(H * W - 1) * 4 + 3] > 250;
        var byAlpha = alpha && !opaque;
        for (var y = 0; y < H; y++) for (var i = 0; i < W; i++) {
          var q = (y * W + i) * 4;
          if (p[q + 3] > 24 && (byAlpha || Math.min(p[q], p[q + 1], p[q + 2]) < 242)) {
            if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
          }
        }
        if (x1 < 0) return;
        var wf = (x1 + 1 - x0) / W, hf = (y1 + 1 - y0) / H;
        var m = [(x0 / W).toFixed(4), (y0 / H).toFixed(4), wf.toFixed(4), hf.toFixed(4),
          ((wf * W) / (hf * H)).toFixed(4), (probe.naturalWidth / probe.naturalHeight).toFixed(4)];
        if (opaque) m.push(1);
        fcache[k] = m; fsave();
        applyFit(img, m);
      } catch (e) { /* tainted canvas: keep the plain contain */ }
    };
    probe.src = /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=160') : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160';
  }
  var fitIO = null;
  function initFit(ctx) {
    if (!FIT_ON) return;
    var imgs = all('img[data-o2-fit], img[data-fit]', ctx).filter(function (i) { return !i.__o2fit; });
    imgs.forEach(function (i) { i.__o2fit = true; });
    if (!('IntersectionObserver' in w)) { imgs.forEach(fit); return; }
    if (!fitIO) fitIO = new IntersectionObserver(function (es) {
      es.forEach(function (en) { if (en.isIntersecting) { fitIO.unobserve(en.target); fit(en.target); } });
    }, { rootMargin: '600px 0px' });
    imgs.forEach(function (i) { fitIO.observe(i); });
  }

  /* ------------------------------------------------------------ cut-out check (2026-10-10, additive) */
  var CUT = {}; /* url key (no width) -> true (cut-out) | false (opaque or unreadable) | [callbacks] while probing */
  function idle(fn) { if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 2500 }); else setTimeout(fn, 250); }
  function cutProbe(src, done) {
    var k = fkey(src), c = CUT[k];
    if (c === true || c === false) { done(c); return; }
    if (c) { c.push(done); return; }
    CUT[k] = [done];
    function fin(v) {
      var q = CUT[k];
      CUT[k] = v;
      q.forEach(function (f) { try { f(v); } catch (e) {} });
    }
    var p = new Image();
    p.crossOrigin = 'anonymous';
    p.onload = function () {
      var v = false;
      try {
        var W = 48, H = Math.max(1, Math.round(48 * p.naturalHeight / p.naturalWidth));
        var cv = d.createElement('canvas'); cv.width = W; cv.height = H;
        var x = cv.getContext('2d', { willReadFrequently: true });
        x.drawImage(p, 0, 0, W, H);
        var a = x.getImageData(0, 0, W, H).data;
        v = a[3] < 250 || a[(W - 1) * 4 + 3] < 250 || a[(H - 1) * W * 4 + 3] < 250 || a[(H * W - 1) * 4 + 3] < 250;
      } catch (e) { v = false; /* tainted canvas: keep the white plate */ }
      fin(v);
    };
    p.onerror = function () { fin(false); };
    p.src = /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=48') : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=48';
  }
  function cutCheck(img) {
    var src = img.currentSrc || img.src;
    if (!src || /^data:/.test(src)) return;
    var k = fkey(src);
    cutProbe(src, function (v) {
      /* the picture may have swapped source while this one was probed: only the current URL's answer counts */
      if (fkey(img.currentSrc || img.src) !== k) return;
      var pl = img.closest('[data-o2-plate]') || img.parentNode;
      if (pl && pl.classList) pl.classList.toggle('is-cutout', v === true);
    });
  }
  function initCutout(ctx) {
    var imgs = all('img[data-o2-cutout-check]', ctx).filter(function (i) { return !i.__o2cut; });
    if (!imgs.length) return;
    imgs.forEach(function (i) { i.__o2cut = true; });
    idle(function () {
      imgs.forEach(function (i) {
        try {
          i.addEventListener('load', function () { cutCheck(i); });
          if (i.complete && i.naturalWidth) cutCheck(i);
        } catch (e) {}
      });
    });
  }

  /* ------------------------------------------------------------ reveal */
  var rvIO = null;
  function show(el) {
    if (el.classList.contains('in')) return;
    el.classList.add('in');
    var ms = parseInt(el.getAttribute('data-rv-done'), 10);
    setTimeout(function () { el.classList.add('done'); }, isNaN(ms) ? 2600 : ms);
  }
  function initReveal(ctx) {
    var els = all('[data-rv]', ctx).filter(function (e) { return !e.__o2rv; });
    els.forEach(function (e) { e.__o2rv = true; });
    if (reduced() || !('IntersectionObserver' in w)) { els.forEach(show); return; }
    var vh = w.innerHeight || root.clientHeight;
    if (!rvIO) rvIO = new IntersectionObserver(function (es) {
      es.forEach(function (en) { if (en.isIntersecting) { rvIO.unobserve(en.target); show(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
    els.forEach(function (e) {
      /* already on screen at start-up: show now, so html.o2-js never flashes it out */
      var r = e.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0 && visible(e)) show(e); else rvIO.observe(e);
    });
  }

  /* ------------------------------------------------------------ add to cart */
  var live = null;
  function announce(t) {
    if (!live) {
      live = d.createElement('div');
      live.className = 'o2-sr'; live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite');
      d.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = t; }, 60);
  }
  function onSubmit(e) {
    var form = e.target && e.target.closest ? e.target.closest('form[data-o2-atc]') : null;
    if (!form) return;
    var btn = form.querySelector('[type=submit]'), idEl = form.querySelector('input[name=id]');
    if (!btn || !idEl || !w.fetch) return;
    e.preventDefault();
    if (btn.disabled) return;
    var title = btn.getAttribute('data-title') || 'Item', label = btn.getAttribute('aria-label');
    var url = ((w.routes && w.routes.cartAddUrl) || form.getAttribute('action') || '/cart/add').replace(/\.js$/, '') + '.js';
    btn.disabled = true;
    btn.setAttribute('aria-label', 'Adding ' + title + ' to basket');
    fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(idEl.value, 10), quantity: 1 })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); }).then(function () {
      btn.disabled = false;
      btn.classList.add('is-done');
      btn.setAttribute('aria-label', 'Added ' + title + ' to basket');
      announce(title + ' added to your basket');
      setTimeout(function () { btn.classList.remove('is-done'); if (label === null) btn.removeAttribute('aria-label'); else btn.setAttribute('aria-label', label); }, 2000);
      /* the theme's own event: it refreshes the header count + mini-cart, and opens the drawer when Cart type = Drawer.
         button: null on purpose: in Cart type = Message the theme would swap the button's innerHTML for the text
         "Added!", which does not fit a 42px icon circle; the tick above is this card's confirmation instead. */
      root.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { button: null, variant: null, quantity: 1, productAdded: true } }));
    }).catch(function () {
      btn.disabled = false;
      btn.classList.add('is-error');
      btn.setAttribute('aria-label', 'Could not add ' + title + ' to basket. Try again.');
      announce('Sorry, ' + title + ' could not be added. Try again.');
      setTimeout(function () { btn.classList.remove('is-error'); if (label === null) btn.removeAttribute('aria-label'); else btn.setAttribute('aria-label', label); }, 3000);
    });
  }

  /* ------------------------------------------------------------ Ask Otto (2026-10, additive) */
  function euOpenOtto(opts, el) {
    opts = opts || {};
    if (el) opts.opener = el;
    if (w.EuronicsAI && typeof w.EuronicsAI.open === 'function') { w.EuronicsAI.open(opts); return true; }
    var l = d.querySelector('.eai-launcher__open'); if (l) { l.click(); return true; }
    return false;
  }
  function ottoReady() {
    return !!((w.EuronicsAI && typeof w.EuronicsAI.open === 'function') || d.querySelector('.eai-launcher__open'));
  }
  function onOttoClick(e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-o2-otto]') : null;
    if (!b) return;
    e.preventDefault();
    var o = { source: b.getAttribute('data-o2-otto-source') || 'home' };
    var sub = b.getAttribute('data-o2-otto-submit'), pre = b.getAttribute('data-o2-otto-prefill');
    if (sub) o.submit = sub; else if (pre) o.prefill = pre;
    if (!euOpenOtto(o, b)) w.location.href = b.getAttribute('data-o2-otto-href') || '/pages/ai';
  }
  function ottoGate() {
    if (w.Shopify && w.Shopify.designMode) return;
    setTimeout(function () {
      if (ottoReady()) return;
      all('[data-o2-otto-only]').forEach(function (el) { el.hidden = true; });
    }, 3000);
  }

  /* ------------------------------------------------------------ boot */
  function init(ctx) {
    ctx = ctx || d;
    all('[data-o2-tabs]', ctx).forEach(initTabs);
    var scopes = [];
    all('[data-o2-rail], [data-o2-dir]', ctx).forEach(function (el) { var s = scopeOf(el); if (scopes.indexOf(s) < 0) scopes.push(s); });
    scopes.forEach(initScope);
    all('[data-o2-dots]', ctx).forEach(initDots);
    initFit(ctx);
    try { initCutout(ctx); } catch (e) {}
    initReveal(ctx);
  }
  function boot() {
    init(d);
    root.classList.add('o2-js');
  }
  if (!w.__eurO2) {
    w.__eurO2 = true;
    d.addEventListener('submit', onSubmit);
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot); else boot();
    d.addEventListener('shopify:section:load', function (e) { init(e.target); });
    d.addEventListener('shopify:block:select', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var tab = t.matches && t.matches('[role=tab]') ? t : null;
      var tl = tab && tab.closest('[data-o2-tabs]');
      if (tl && tl.__o2select) tl.__o2select(all('[role=tab]', tl).indexOf(tab));
    });
  }
  /* 2026-10: Ask Otto buttons + the Otto-only gate (own guard, so a second copy of this file adds them only once) */
  if (!w.__eurO2Otto) {
    w.__eurO2Otto = true;
    d.addEventListener('click', onOttoClick);
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', ottoGate); else ottoGate();
  }
}());
